/**
 * A money value.
 *
 * Delegates to `lib/format` rather than formatting inline — that module owns
 * PRD §9.5, and a component that formats its own numbers is a component that
 * will drift from it.
 *
 * Tabular figures so a column of amounts aligns on the decimal point; a
 * proportional column of digits is unreadable when scanned vertically.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.16, PRD §9.5
 */
import { cn } from '../../lib/cn';
import { formatMoney, type MoneyStyle } from '../../lib/format';

export interface MoneyProps {
  /** Amount in euros. Null renders the empty marker. */
  value: number | null | undefined;
  /** `table` abbreviates (€340k); `exact` writes it in full (€340,000). */
  style?: MoneyStyle;
  /** Shown when there is no value. */
  emptyText?: string;
  className?: string;
}

export function Money({
  value,
  style = 'table',
  emptyText = '—',
  className,
}: MoneyProps): React.JSX.Element {
  const formatted =
    value === null || value === undefined ? '' : formatMoney(value, style);

  return (
    <span className={cn('tabular-nums', className)}>
      {formatted === '' ? emptyText : formatted}
    </span>
  );
}
